<script lang="ts" setup>
import { computed, onBeforeMount, onMounted, watch } from 'vue'

import { AdWrapper, BlockContainer, Heading } from '@toni/brand-ui'

import { useSourcepointStore } from '../../stores/sourcepoint'
import { usePageStore } from '../../stores/page'
import { useSessionStore } from '../../stores/session'
import { useConfigStore } from '../../stores/config'
import { usePageBlockWidth } from '../../composables/use-page-block-width.ts'
import RenderContentBlocks from '../../content-blocks/RenderContentBlocks.vue'
import Agf from '../../tracking/components/Agf.vue'
import { useClientScriptLoader } from '../../tracking/composables/client-script-loader.ts'
import { useArticleTracking } from '../../tracking/composables/use-article-tracking'
import { useSponsoredLinkTracking } from '../../tracking/composables/use-sponsored-link-tracking'
import { MetaData } from '../../utils/meta-data-schema'
import { PageBody } from '../../utils/page-body-schema'
import { PageMeta } from '../../utils/page-meta-schema'
import { useAdProvider } from '../../utils/use-ad-provider'
import AdTaboola from '../ad/AdTaboola.vue'
import FooterAd from '../ad/FooterAd.vue'
import HeaderAd from '../ad/HeaderAd.vue'
import SkyscraperAd from '../ad/SkyscraperAd.vue'
import BlockExternalHeaderBanner from '../content-blocks/BlockExternalHeaderBanner.vue'
import BlockAdvertorialBadge from '../page-blocks/BlockAdvertorialBadge.vue'
import BlockAuthorHeader from '../page-blocks/BlockAuthorHeader.vue'
import BlockBreadcrumb from '../page-blocks/BlockBreadcrumb.vue'
import BlockPagination from '../page-blocks/BlockPagination.vue'
import BlockSeoText from '../page-blocks/BlockSeoText.vue'
import BlockTagList from '../page-blocks/BlockTagList.vue'
import ClientOnly from '../client-only/ClientOnly.vue'
import BlockAffiliateDisclaimer from '../page-blocks/BlockAffiliateDisclaimer.vue'

const props = defineProps<{
  pageData: PageBody,
  pageMeta: PageMeta,
  metaData: MetaData,
  brand: string
}>()

const brand = computed(() => props.brand)
const pageStore = usePageStore()
const sessionStore = useSessionStore()
const sourcepointStore = useSourcepointStore()
const isHome = computed(() => pageStore.isHome as boolean)
const rsConfig = useConfigStore().rsConfig
const simulateContentPassOrContentPass = computed(() => sessionStore.simulateContentPassOrContentPass)
useClientScriptLoader()

const ogImage = computed(() => {
  return props.metaData.ogImage ?? {}
})

const { initAdProvider } = useAdProvider(brand.value)

const paragraphs = computed(() => {
  return props.pageData.paragraphs
})

const computedBreadcrumbs = computed(() => {
  let breadcrumbs = props.pageData.mainStructurePath

  if (breadcrumbs?.[0]?.url === '/') {
    breadcrumbs = breadcrumbs?.slice(1)
  }

  if (props.pageMeta.pageType === 'structureterm' && (breadcrumbs?.length ?? 0) > 0) {
    breadcrumbs = breadcrumbs?.slice(0, -1)
  }

  return breadcrumbs ?? []
})

const computedSocialStrip = computed(() => {
  const socialStripObject = (paragraphs.value ?? []).find((p: { paragraphType: string }) => p.paragraphType === 'socialStrip')
  return socialStripObject && 'socialStrip' in socialStripObject ? socialStripObject.socialStrip : []
})

const seoText = computed(() => props.pageData?.seoText)

const size = computed(() => usePageBlockWidth(pageStore.pageData?.pageMeta, isHome.value, brand.value).value)

const consentedVendors = computed(() => sourcepointStore.consentedVendors)
const adaId = rsConfig.consent.vendor.ada as string
const adaHasConsent = computed(() => {
  return consentedVendors.value.includes(adaId)
})

const enableAda = computed(() => {
  const isGerman = process.env.APP_LOCALE === 'de-DE'
  return isGerman && !simulateContentPassOrContentPass.value && adaHasConsent.value
})

const shouldShowVgWort = computed(() => {
  const isGerman = process.env.APP_LOCALE === 'de-DE'
  const isArticle = props.pageMeta.pageType === 'article'
  const hasTrackingId = Boolean(props.pageMeta.vgWortTrackingId)

  return isGerman && isArticle && hasTrackingId
})

watch(enableAda, (newValue) => {
  if (newValue && adaHasConsent.value) {
    initAdProvider()
  }
})

onBeforeMount(() => {
  // This function is used to update the simulate content pass status from the URL
  sessionStore.updateSimulateContentPassParamFromUrl()
})

onMounted(() => {
  if (enableAda.value) {
    initAdProvider()
  }
})

useArticleTracking(brand.value)

useSponsoredLinkTracking()
</script>

<template>
  <main data-testid="article-body"
        class="relative max-w-[1040px] mr-auto px-4 sm:px-5 lg:max-w-[1320px] lg:mr-[200px] xl:ml-auto 2xl:mr-auto">
    <img v-if="shouldShowVgWort"
         :src="`https://vg01.met.vgwort.de/na/${props.pageMeta.vgWortTrackingId}`"
         height="1"
         width="1"
         border="0"
         alt=""
         class="hidden">
    <ClientOnly>
      <template #default>
        <HeaderAd />
      </template>
      <template #fallback>
        <!-- Render a empty ad slot on server to avoid layout shift -->
        <AdWrapper slot-name="combined_desktop_ads" />
      </template>
    </ClientOnly>
    <SkyscraperAd />
    <BlockBreadcrumb v-if="props.pageData.mainStructurePath && props.pageMeta.url !== '/' && !['author', 'static'].includes(props.pageMeta.structureTermType ?? '')"
                     :breadcrumbs="computedBreadcrumbs" />
    <BlockAdvertorialBadge v-if="props.metaData.advertorial" />

    <!--Only used by sweepstakes: display addtional brand-specific header image-->
    <BlockExternalHeaderBanner v-if="props.pageMeta.pageType === 'structureterm' && props.pageData?.externalHeaderBanner"
                               :image="props.pageData.externalHeaderBanner"
                               :page-type="props.pageMeta.pageType" />
    <!--Brand-specific article header slot-->
    <slot name="article-header" />

    <BlockContainer v-show="['static', 'structure'].includes(props.pageMeta?.structureTermType ?? '') &&
                      props.pageData?.header?.headline &&
                      props.pageMeta.url !== '/' &&
                      !isHome"
                    :size="size"
                    class="my-4 md:my-8">
      <Heading :variant="'h1'">
        {{ props.pageData.header?.headline }}
      </Heading>
    </BlockContainer>
    <BlockAuthorHeader v-if="props.pageMeta.structureTermType === 'author'"
                       :headline="props.pageData.header?.headline ?? ''"
                       :job-title="props.pageData.jobTitle ?? ''"
                       :image="ogImage"
                       :social-strip="computedSocialStrip"
                       :brand="brand" />
    <RenderContentBlocks />
    <BlockPagination v-if="(props.pageMeta.numPages ?? 0) > 1"
                     :page-meta="props.pageMeta" />
    <BlockAffiliateDisclaimer v-if="props.metaData?.showAffiliateDisclaimer" />
    <AdTaboola v-if="props.pageMeta.pageType === 'article' && !props.metaData.adaAutoNative" />
    <BlockTagList
      v-if="props.pageMeta.pageType === 'article'"
      :page-data="pageStore.pageData.body"
      :brand="brand" />
    <BlockSeoText v-if="seoText?.length"
                  :seo-text="seoText" />
    <FooterAd />
    <Agf :meta-data="props.metaData"
         :page-data="props.pageData"
         :page-meta="props.pageMeta"
         :brand="brand" />
  </main>
</template>
